/**
 * Custom React reconciler for JSX-to-string instruction rendering.
 *
 * Uses react-reconciler directly to build an internal tree from JSX,
 * then serialize to formatted text with priority-ordered XML sections.
 *
 * @example
 * ```tsx
 * const output = renderInstructions(
 *   <>
 *     <Identity priority={10}>You are Atlas...</Identity>
 *     <Constraints priority={20}>
 *       <ul>
 *         <li>Never fabricate information</li>
 *       </ul>
 *     </Constraints>
 *   </>
 * );
 * ```
 */

import type { ReactNode } from 'react';
import ReactReconciler from 'react-reconciler';

import type {
  Node,
  ParentNode,
  TableCellHeaderNode,
  TableCellNode,
  TableNode,
  TableRowNode,
  TextNode,
} from './types.ts';

type ElementType = string | ((props: Record<string, unknown>) => ReactNode);
type Props = Record<string, unknown>;

const nodeCreators: Record<string, (props: Props) => Node> = {
  instruction: (props) => ({
    kind: 'section',
    tag: String(props.tag ?? ''),
    children: [],
  }),
  h1: () => ({ kind: 'header', level: 1, children: [] }),
  h2: () => ({ kind: 'header', level: 2, children: [] }),
  h3: () => ({ kind: 'header', level: 3, children: [] }),
  h4: () => ({ kind: 'header', level: 4, children: [] }),
  h5: () => ({ kind: 'header', level: 5, children: [] }),
  h6: () => ({ kind: 'header', level: 6, children: [] }),
  header: () => ({ kind: 'section-header', children: [] }),
  ul: () => ({ kind: 'list', ordered: false, children: [] }),
  ol: () => ({ kind: 'list', ordered: true, children: [] }),
  li: () => ({ kind: 'list-item', children: [] }),
  p: () => ({ kind: 'paragraph', children: [] }),
  pre: () => ({ kind: 'pre', children: [] }),
  example: (props) => ({
    kind: 'example',
    name: props.name == null ? undefined : String(props.name),
    children: [],
  }),
  dl: () => ({ kind: 'definition-list', children: [] }),
  dt: () => ({ kind: 'definition-term', children: [] }),
  dd: () => ({ kind: 'definition-description', children: [] }),
  strong: () => ({ kind: 'strong', children: [] }),
  b: () => ({ kind: 'strong', children: [] }),
  em: () => ({ kind: 'emphasis', children: [] }),
  i: () => ({ kind: 'emphasis', children: [] }),
  code: () => ({ kind: 'inline-code', children: [] }),
  table: () => ({ kind: 'table', children: [] }),
  thead: () => ({ kind: 'table-head', children: [] }),
  tbody: () => ({ kind: 'table-body', children: [] }),
  tr: () => ({ kind: 'table-row', children: [] }),
  th: () => ({ kind: 'table-cell-header', children: [] }),
  td: () => ({ kind: 'table-cell', children: [] }),
};

const createNode = (type: ElementType, props: Props): Node => {
  if (typeof type === 'string') {
    const creator = nodeCreators[type];
    if (creator) {
      return creator(props);
    }
    if (type.startsWith('tag-')) {
      const tag = type.slice(4).replaceAll('-', '_');
      return { kind: 'section', tag, children: [] };
    }
  }
  return { kind: 'fragment', children: [] };
};

const isParentNode = (node: Node): node is ParentNode => 'children' in node;

const omitChildren = (props: Props): Props => {
  const { children: _, ...rest } = props;
  return rest;
};

/**
 * Host config for react-reconciler.
 * Implements the minimal interface needed for synchronous tree building.
 */
const hostConfig: ReactReconciler.HostConfig<
  ElementType, // Type
  Props, // Props
  ParentNode, // Container
  Node, // Instance
  TextNode, // TextInstance
  Node, // SuspenseInstance
  Node, // HydratableInstance
  Node, // FormInstance
  Node, // PublicInstance
  object, // HostContext
  Node[], // ChildSet
  ReturnType<typeof setTimeout>, // TimeoutHandle
  -1, // NoTimeout
  null // TransitionStatus
> = {
  supportsMutation: true,
  supportsPersistence: false,
  supportsHydration: false,
  isPrimaryRenderer: false, // Must be false for synchronous rendering without ReactDOM

  // Timing
  scheduleTimeout: setTimeout,
  cancelTimeout: clearTimeout,
  noTimeout: -1,
  supportsMicrotasks: true,
  scheduleMicrotask: globalThis.queueMicrotask ?? ((fn) => Promise.resolve().then(fn)),

  // Context
  getRootHostContext: () => ({}),
  getChildHostContext: (parentHostContext) => parentHostContext,
  getPublicInstance: (instance) => instance,

  // Instance creation
  createInstance: (type, props) => createNode(type, omitChildren(props)),
  createTextInstance: (text): TextNode => ({ kind: 'text', value: text }),

  // Tree building
  appendInitialChild: (parentInstance, child) => {
    if (isParentNode(parentInstance)) {
      parentInstance.children.push(child);
    }
  },
  appendChild: (parentInstance, child) => {
    if (isParentNode(parentInstance)) {
      parentInstance.children.push(child);
    }
  },
  appendChildToContainer: (container, child) => {
    container.children.push(child);
  },

  // Insertion
  insertBefore: (parentInstance, child, beforeChild) => {
    if (!isParentNode(parentInstance)) {
      return;
    }
    const idx = parentInstance.children.indexOf(beforeChild);
    if (idx >= 0) {
      parentInstance.children.splice(idx, 0, child);
    } else {
      parentInstance.children.push(child);
    }
  },
  insertInContainerBefore: (container, child, beforeChild) => {
    const idx = container.children.indexOf(beforeChild);
    if (idx >= 0) {
      container.children.splice(idx, 0, child);
    } else {
      container.children.push(child);
    }
  },

  // Removal
  removeChild: (parentInstance, child) => {
    if (!isParentNode(parentInstance)) {
      return;
    }
    const idx = parentInstance.children.indexOf(child);
    if (idx >= 0) {
      parentInstance.children.splice(idx, 1);
    }
  },
  removeChildFromContainer: (container, child) => {
    const idx = container.children.indexOf(child);
    if (idx >= 0) {
      container.children.splice(idx, 1);
    }
  },

  // Finalization
  finalizeInitialChildren: () => false,
  prepareForCommit: () => null,
  resetAfterCommit: () => {
    /* noop */
  },
  preparePortalMount: () => {
    /* noop */
  },

  // Text content
  shouldSetTextContent: () => false,
  resetTextContent: () => {
    /* noop */
  },

  // Updates - commitUpdate signature changed in newer types
  commitUpdate: (instance, _type, _prevProps, nextProps) => {
    if (instance.kind === 'section' && nextProps.tag != null) {
      instance.tag = String(nextProps.tag);
    }
    if (instance.kind === 'example' && nextProps.name !== undefined) {
      instance.name = nextProps.name == null ? undefined : String(nextProps.name);
    }
  },
  commitTextUpdate: (textInstance, _oldText, newText) => {
    textInstance.value = newText;
  },

  // Container
  clearContainer: (container) => {
    container.children = [];
  },

  // Instance management
  getInstanceFromNode: () => null,
  beforeActiveInstanceBlur: () => {
    /* noop */
  },
  afterActiveInstanceBlur: () => {
    /* noop */
  },
  prepareScopeUpdate: () => {
    /* noop */
  },
  getInstanceFromScope: () => null,
  detachDeletedInstance: () => {
    /* noop */
  },

  // Priority
  setCurrentUpdatePriority: () => {
    /* noop */
  },
  getCurrentUpdatePriority: () => 16,
  resolveUpdatePriority: () => 16,

  // Form
  resetFormInstance: () => {
    /* noop */
  },

  // Callbacks
  requestPostPaintCallback: () => {
    /* noop */
  },

  // Transition
  shouldAttemptEagerTransition: () => false,
  trackSchedulerEvent: () => {
    /* noop */
  },
  resolveEventType: () => null,
  resolveEventTimeStamp: () => -1.1,

  // Suspense
  maySuspendCommit: () => false,
  preloadInstance: () => true,
  startSuspendingCommit: () => {
    /* noop */
  },
  suspendInstance: () => {
    /* noop */
  },
  waitForCommitToBeReady: () => null,

  // Transition context
  NotPendingTransition: null,
  HostTransitionContext: {
    // biome-ignore lint/style/useNamingConvention: Required by react-reconciler
    $$typeof: Symbol.for('react.context'),
    Consumer: null as unknown as ReactReconciler.ReactContext<null>,
    Provider: null as unknown as ReactReconciler.ReactProviderType<null>,
    _currentValue: null,
    _currentValue2: null,
    _threadCount: 0,
  },
};

// Create reconciler instance
// Note: @types/react-reconciler says flushSync exists, but runtime version 0.33.0
// exports it as flushSyncWork/updateContainerSync. We use type augmentation to access
// the actual runtime API until types are updated.
const reconcilerInstance = ReactReconciler(hostConfig);
type ReconcilerWithSync = typeof reconcilerInstance & {
  updateContainerSync: typeof reconcilerInstance.updateContainer;
  flushSyncWork: () => void;
};
const reconciler = reconcilerInstance as ReconcilerWithSync;

/**
 * Sentinel marking a pre-formatted region in serialized output.
 *
 * `<pre>` wraps its body with a matched pair of these so the final
 * whitespace pass in {@link normalizeOutput} can skip it — multi-space
 * collapse and space-before-punctuation stripping would otherwise destroy
 * code indentation. NUL is used because it cannot appear in source text.
 */
const RAW_SENTINEL = '\x00';

/**
 * Final whitespace normalization, applied once at the root.
 *
 * Splits on the {@link RAW_SENTINEL} so segments inside pre-formatted
 * regions are preserved verbatim while prose segments get the cleanup
 * that compensates for JSX whitespace stripping.
 */
const normalizeOutput = (s: string): string =>
  s
    .split(RAW_SENTINEL)
    .map((segment, i) =>
      i % 2 === 0
        ? segment
            .replace(/[ \t]{2,}/g, ' ')
            // .replace(/ ([,;.!?:)\]}])/g, '$1')
            .replace(/\n{3,}/g, '\n\n')
        : segment,
    )
    .join('');

/**
 * Raw join — preserves literal whitespace. Used for `<pre>` and other
 * pre-formatted contexts where indentation and runs of spaces matter.
 */
const serializeRawChildren = (children: Node[]): string => children.map(serializeNode).join('');

/**
 * Inline-aware join.
 *
 * JSX strips newlines adjacent to tags (whitespace rule #4), so an
 * element separated from a following sibling only by a source newline
 * loses the natural separator. This join re-injects a single space at
 * those boundaries — but ONLY when at least one side is a non-text
 * node (an element). Two adjacent **text** nodes are emitted verbatim:
 * JSX already baked the author's intended spacing into the string
 * literals, so `{a}{b}` / `{n} step{plural}` must stay glued (inserting
 * a space there produced bugs like `( 5 step s ,`). A synthetic space
 * is likewise suppressed when the right side opens with closing/sep
 * punctuation (`,`, `.`, `)`, …) so `<code>x</code>,` stays `\`x\`,`.
 *
 * Final whitespace normalization (multi-space collapse, 3+-newline
 * collapse) is applied once at the root by {@link normalizeOutput};
 * doing it here would recurse into already-serialized `<pre>` content
 * and destroy code indentation.
 */
const NO_SPACE_BEFORE = /^[,;.!?:)\]}]/;

const serializeChildren = (children: Node[]): string => {
  let joined = '';
  let prevWasText = false;
  for (const node of children) {
    const part = serializeNode(node);
    if (!part) {
      continue;
    }
    const isText = node.kind === 'text';
    if (joined === '') {
      joined = part;
      prevWasText = isText;
      continue;
    }
    const leftEnd = joined.slice(-1);
    const rightStart = part.charAt(0);
    const glue = /\s/.test(leftEnd) || /\s/.test(rightStart) || (prevWasText && isText) || NO_SPACE_BEFORE.test(part);
    joined += glue ? part : ` ${part}`;
    prevWasText = isText;
  }
  return joined;
};

const escapeTableCell = (s: string): string => s.replace(/\n/g, ' ').replace(/\|/g, '\\|');

const tableRowCells = (row: TableRowNode): string[] =>
  row.children
    .filter((c): c is TableCellNode | TableCellHeaderNode => c.kind === 'table-cell-header' || c.kind === 'table-cell')
    .map((c) => escapeTableCell(serializeChildren(c.children).trim()));

const partitionTableRows = (children: Node[]): { headRows: TableRowNode[]; bodyRows: TableRowNode[] } => {
  const headRows: TableRowNode[] = [];
  const bodyRows: TableRowNode[] = [];
  for (const child of children) {
    if (child.kind === 'table-head' || child.kind === 'table-body') {
      const target = child.kind === 'table-head' ? headRows : bodyRows;
      for (const row of child.children) {
        if (row.kind === 'table-row') {
          target.push(row);
        }
      }
    } else if (child.kind === 'table-row') {
      bodyRows.push(child);
    }
  }
  // HTML-table convention: if no <thead> was given but the first body
  // row uses <th> cells, treat that row as the header.
  const firstBody = bodyRows[0];
  if (headRows.length === 0 && firstBody && firstBody.children.some((c) => c.kind === 'table-cell-header')) {
    headRows.push(firstBody);
    bodyRows.shift();
  }
  return { headRows, bodyRows };
};

const serializeTable = (node: TableNode): string => {
  const { headRows, bodyRows } = partitionTableRows(node.children);
  const headCells = headRows.flatMap(tableRowCells);
  const bodyCellRows = bodyRows.map(tableRowCells);
  const colCount = Math.max(headCells.length, ...bodyCellRows.map((r) => r.length), 1);
  const pad = (cells: string[]): string[] =>
    cells.length >= colCount ? cells.slice(0, colCount) : [...cells, ...Array(colCount - cells.length).fill('')];
  const headerCells =
    headCells.length > 0 ? pad(headCells) : Array.from({ length: colCount }, (_, i) => `Column ${i + 1}`);
  const lines = [
    `| ${headerCells.join(' | ')} |`,
    `| ${Array(colCount).fill('---').join(' | ')} |`,
    ...bodyCellRows.map((row) => `| ${pad(row).join(' | ')} |`),
  ];
  return `\n\n${lines.join('\n')}\n\n`;
};

const serializeNode = (node: Node): string => {
  switch (node.kind) {
    case 'text':
      return node.value;

    case 'section': {
      const content = serializeChildren(node.children).trim();
      return `\n\n<${node.tag}>\n${content}\n</${node.tag}>\n\n`;
    }

    case 'section-header': {
      const content = serializeChildren(node.children).trim();
      return `\n**${content}:**\n\n`;
    }

    case 'header': {
      const prefix = '#'.repeat(node.level);
      const content = serializeChildren(node.children).trim();
      return `\n\n${prefix} ${content}\n\n`;
    }

    case 'paragraph':
      return `\n\n${serializeChildren(node.children).trim()}\n\n`;

    case 'pre':
      return `\`\`\`${node.language ?? ''}
${RAW_SENTINEL}${serializeRawChildren(node.children).trim()}${RAW_SENTINEL}
\`\`\`\n\n`;

    case 'list':
      return `${node.children
        .map((child, i) => {
          const bullet = node.ordered ? `${i + 1}.` : '-';
          return `${bullet} ${serializeNode(child).trim()}`;
        })
        .join('\n')}\n`;

    case 'list-item':
      return serializeChildren(node.children);

    case 'example': {
      return `\n\n<example workflow="${node.name}">\n${serializeChildren(node.children).trim()}\n</example>\n\n`;
    }

    case 'definition-list': {
      // A <dt> opens an entry; every <dd> up to the next <dt> belongs
      // to it (a term may have multiple descriptions — e.g. an op's
      // signature, prose, per-kwarg lines, and a code example). A <dd>
      // with no preceding <dt> is a free-standing description.
      type Entry = { readonly term: string | null; readonly descs: string[] };
      const entries: Entry[] = [];
      for (const child of node.children) {
        if (child.kind === 'definition-term') {
          entries.push({ term: serializeChildren(child.children).trim(), descs: [] });
          continue;
        }
        if (child.kind !== 'definition-description') {
          continue;
        }
        const desc = serializeChildren(child.children).trim();
        const open = entries.at(-1);
        if (open === undefined || open.term === null) {
          entries.push({ term: null, descs: [desc] });
        } else {
          open.descs.push(desc);
        }
      }
      // A description is "block" when it is a fenced code region (a
      // `<pre>` example): it must sit on its own lines, never glued to
      // a `- ` bullet or indented as inline continuation.
      const isBlock = (d: string): boolean => d.startsWith('```') || d.includes(RAW_SENTINEL);
      const renderEntry = (e: Entry): string => {
        if (e.term === null) {
          return e.descs.map((d) => (isBlock(d) ? `\n${d}\n` : `- ${d}`)).join('\n');
        }
        if (e.descs.length === 0) {
          return `- ${e.term}`;
        }
        // Compact form: a lone inline description stays on the term
        // line (the shape ToolGuidance / RuleList emit).
        if (e.descs.length === 1 && e.descs[0] !== undefined && !isBlock(e.descs[0])) {
          return `- ${e.term}: ${e.descs[0]}`;
        }
        const body = e.descs
          .map((d) => (isBlock(d) ? `\n${d}\n` : `  - ${d}`))
          .join('\n')
          .replace(/\n{3,}/g, '\n\n');
        return `- ${e.term}\n${body}`;
      };
      return `${entries.map(renderEntry).join('\n')}\n`;
    }

    case 'definition-term':
    case 'definition-description':
      return serializeChildren(node.children);

    case 'strong':
      return `*${serializeChildren(node.children).trim()}*`;

    case 'emphasis':
      return `_${serializeChildren(node.children).trim()}_`;

    case 'inline-code':
      return `\`${serializeChildren(node.children).trim()}\``;

    case 'fragment':
      return serializeChildren(node.children);

    case 'table':
      return serializeTable(node);

    case 'table-head':
    case 'table-body':
    case 'table-row':
    case 'table-cell-header':
    case 'table-cell':
      return serializeChildren(node.children);
  }
};

/**
 * Renders JSX instructions to a formatted string.
 *
 * Content is serialized in document order. `<tag-foo>` / `<instruction tag="foo">`
 * elements wrap their children in `<foo>…</foo>` XML. Other elements produce
 * markdown-style formatting (headers, lists, paragraphs).
 */
export function renderInstructions(element: ReactNode): string {
  const root: ParentNode = { kind: 'fragment', children: [] };
  // React's sync reconciler reports render-time throws through these
  // callbacks instead of propagating them out of flushSyncWork. Capturing
  // the first error and rethrowing it below ensures a throwing node can
  // never silently collapse to empty output (which a downstream blank
  // filter would then drop without a trace).
  let capturedError: unknown;
  let hasError = false;
  const captureError = (error: unknown) => {
    if (!hasError) {
      hasError = true;
      capturedError = error;
    }
  };
  const container = reconciler.createContainer(
    root,
    0, // LegacyRoot tag for sync rendering
    null, // hydrationCallbacks
    false, // isStrictMode
    null, // concurrentUpdatesByDefaultOverride
    '', // identifierPrefix
    captureError, // onUncaughtError
    captureError, // onCaughtError
    () => {
      /* noop — recoverable errors are non-fatal */
    }, // onRecoverableError
    () => {
      /* noop */
    }, // onDefaultTransitionIndicator
  );

  // Use updateContainerSync + flushSyncWork for truly synchronous rendering
  reconciler.updateContainerSync(element, container, null);
  reconciler.flushSyncWork();

  if (hasError) {
    throw new Error('renderInstructions failed to render node', { cause: capturedError });
  }

  return normalizeOutput(serializeChildren(root.children)).trim();
}
